import * as React from "react";
import { useEffect } from "react";
import styled from "styled-components";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { Spinner } from "components/common";
import { Alert } from "components/bootstrap";
import { Col, Row } from "components/lookup-tables/layout-componets";
import { ErrorsProvider } from "components/lookup-tables/contexts/ErrorsContext";
import ErrorsConsumer from "components/lookup-tables/lookup-table-list/errors-consumer";
import LookupTableDetails from "components/lookup-tables/lookup-table-view/basic-details";
import PurgeCache from "components/lookup-tables/lookup-table-view/purge-cache";
import TestLookup from "components/lookup-tables/lookup-table-view/test-lookup";
import { useFetchCache, useFetchDataAdapter } from "components/lookup-tables/hooks/useLookupTablesAPI";
import Cache from "components/lookup-tables/Cache";
import DataAdapter from "components/lookup-tables/DataAdapter";
import useDefaultValueValidation from "./use-default-value-validation";
const StyledContainer = styled(Row)`
  max-width: 1500px;
  margin: 0 auto;

  & .content.row {
    width: 100%;
    margin: 0;
    padding: 0;
    box-shadow: none;

    [class*='col-'] {
      padding: 0;
      margin: 0;
    }
  }
`;
const DangerText = styled.i`
  color: ${({ theme }) => theme.colors.variant.danger};
`;
const MuttedText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  width: 50px;
`;
function DefaultValueErrors({ errors }) {
  const { t } = useTranslation("lookup_tables");
  const invalidDefaults = [
    [t("lut_summary.default_single_label"), errors.default_single_value],
    [t("lut_summary.default_multi_label"), errors.default_multi_value]
  ].filter(([, error]) => !!error);
  if (invalidDefaults.length === 0) return null;
  return /* @__PURE__ */ React.createElement(Alert, { style: { width: "100%" }, bsStyle: "danger", title: t("lut_summary.invalid_defaults_title") }, invalidDefaults.map(([label, error]) => /* @__PURE__ */ React.createElement("div", { key: label }, label, ": ", error)), /* @__PURE__ */ React.createElement("div", null, t("lut_summary.fix_defaults")));
}
function TableSummary({ table, errors }) {
  const { t } = useTranslation("lookup_tables");
  return /* @__PURE__ */ React.createElement(Col, { $gap: "lg" }, /* @__PURE__ */ React.createElement(Col, { $gap: "sm" }, /* @__PURE__ */ React.createElement("h2", null, t("lut_summary.table_details")), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(MuttedText, null, t("lut_summary.title_colon")), /* @__PURE__ */ React.createElement("span", null, table.title || /* @__PURE__ */ React.createElement(DangerText, null, t("lut_summary.no_title")))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(MuttedText, null, t("lut_summary.name_colon")), /* @__PURE__ */ React.createElement("span", null, table.name || /* @__PURE__ */ React.createElement(DangerText, null, t("lut_summary.no_name"))))), /* @__PURE__ */ React.createElement(LookupTableDetails, { table }), /* @__PURE__ */ React.createElement(DefaultValueErrors, { errors }), table.id && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(PurgeCache, { table }), /* @__PURE__ */ React.createElement(TestLookup, { table })));
}
function CacheSummary({ cache }) {
  const { t } = useTranslation("lookup_tables");
  if (!cache)
    return /* @__PURE__ */ React.createElement(Alert, { style: { width: "100%" }, bsStyle: "danger" }, t("lut_summary.no_cache_selected"));
  return /* @__PURE__ */ React.createElement(Cache, { cache, noEdit: true });
}
function AdapterSummary({ adapter }) {
  const { t } = useTranslation("lookup_tables");
  if (!adapter)
    return /* @__PURE__ */ React.createElement(Alert, { style: { width: "100%" }, bsStyle: "danger" }, t("lut_summary.no_adapter_selected"));
  return /* @__PURE__ */ React.createElement(DataAdapter, { dataAdapter: adapter, noEdit: true });
}
function SummaryStep() {
  const { t } = useTranslation("lookup_tables");
  const { values, initialValues } = useFormikContext();
  const { defaultValueErrors, validateDefaultValues } = useDefaultValueValidation();
  const { cache, loadingCache } = useFetchCache(values.cache_id);
  const { dataAdapter, loadingDataAdapter } = useFetchDataAdapter(values.data_adapter_id);
  useEffect(() => {
    validateDefaultValues();
  }, []);
  return /* @__PURE__ */ React.createElement(ErrorsProvider, null, /* @__PURE__ */ React.createElement(
    ErrorsConsumer,
    {
      lutNames: initialValues.id && initialValues.name ? [initialValues.name] : void 0,
      cacheNames: (cache == null ? void 0 : cache.name) ? [cache.name] : void 0,
      adapterNames: (dataAdapter == null ? void 0 : dataAdapter.name) ? [dataAdapter.name] : void 0
    }
  ), /* @__PURE__ */ React.createElement(StyledContainer, { $gap: "xl" }, /* @__PURE__ */ React.createElement(TableSummary, { table: values, errors: defaultValueErrors }), /* @__PURE__ */ React.createElement(Col, { $gap: "lg" }, loadingCache ? /* @__PURE__ */ React.createElement(Spinner, { text: t("lut_summary.loading_cache") }) : /* @__PURE__ */ React.createElement(Col, { $gap: "sm" }, /* @__PURE__ */ React.createElement("h2", null, t("lut_summary.cache_details")), /* @__PURE__ */ React.createElement(CacheSummary, { cache })), loadingDataAdapter ? /* @__PURE__ */ React.createElement(Spinner, { text: t("lut_summary.loading_adapter") }) : /* @__PURE__ */ React.createElement(Col, { $gap: "sm" }, /* @__PURE__ */ React.createElement("h2", null, t("lut_summary.adapter_details")), /* @__PURE__ */ React.createElement(AdapterSummary, { adapter: dataAdapter })))));
}
export default SummaryStep;
